import React, { Component } from 'react';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import moment from 'moment';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import WebIcon from '@mui/icons-material/Web';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import BlockIcon from '@mui/icons-material/Block';
import LoginIcon from '@mui/icons-material/Login';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CustomizedSnackbars from '../../components/material-snackbars';
import Api from '../../services/api';
import UsageService from '../../services/usage.service';
import UsagePeriodFilter, { presetRange } from '../../components/usage/usage-period-filter';
import { UsageEllipsisCell, UsageKpiCard, UsageRankBar, UsageHeatmap, UsageEmpty, UsageBlockTitle, KIND_COLORS, API_COLORS, fmtInt, fmtMs, fmtPct, durationColor, kindLabel, UsageSourceTag } from '../../components/usage/usage-widgets';
import UsageExportButton from '../../components/usage/usage-export-button';
import { t } from '../../components/usage/usage-i18n';

// ---------------------------------------------------------------------------
// Dashboard de uso. Lê SÓ o rollup (tb_user_usage_daily) — cada bloco é uma agregação leve.
// Cross-filter: clicar numa tela vira chip no topo e filtra os outros blocos.
// Estado dos filtros na querystring (link compartilhável).
// ---------------------------------------------------------------------------

const HEAVY_MODES = ['avg', 'total', 'max'];
const DENIAL_KINDS = ['denied', 'permission_denied', 'role_denied', 'blocked', 'rate_limited', 'would_deny'];

function parseQuery(search) {
  const q = new URLSearchParams(search || '');
  const preset = q.get('preset') || '7d';
  const range = preset === 'custom' && q.get('start') && q.get('end')
    ? { dateStart: moment(q.get('start')).startOf('day').format('YYYY/MM/DD HH:mm:ss'), dateEnd: moment(q.get('end')).endOf('day').format('YYYY/MM/DD HH:mm:ss') }
    : presetRange(preset);
  return {
    preset, ...range,
    api: q.get('api') || '', actionGroup: q.get('group') || '',
    userId: Number(q.get('userId')) || 0, userName: q.get('userName') || '',
    screenRoute: q.get('screen') || '',
  };
}

export default class UsageDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: parseQuery(props.location?.search),
      loading: true, error: null,
      summary: null, byDay: [], byHour: { byHour: [], heatmap: [] }, topScreens: [], topActions: [], heaviest: [],
      topUsers: [], byEntity: [], denials: { byKind: [], byDay: [], byUser: [] }, byApi: [],
      heavyMode: 'avg', heavyByScreen: false, hourView: 'bars',
      usersSort: { key: 'events', dir: 'desc' }, usersLoading: false,
      snack: { open: false, variant: 'error', message: '' },
      rollup: { lastRollupAt: null, nextAllowedAt: null, retryAfter: 0, cooldownMinutes: 10, running: false, statusLoading: true },
      alerts: null, // item 2: abertos por severidade (Reports usage/alerts/summary); null = API antiga ou falha → cartão some
    };
    this.load = this.load.bind(this);
    this.onFilter = this.onFilter.bind(this);
    this.apply = this.apply.bind(this);
    this.setScreen = this.setScreen.bind(this);
    this.clearScreen = this.clearScreen.bind(this);
    this.loadHeaviest = this.loadHeaviest.bind(this);
    this.goUser = this.goUser.bind(this);
    this.closeSnack = this.closeSnack.bind(this);
    this.runRollup = this.runRollup.bind(this);
    this.unlockRollup = this.unlockRollup.bind(this);
    this.loadRollupStatus = this.loadRollupStatus.bind(this);
    this.tickRollup = this.tickRollup.bind(this);
  }

  componentDidMount() { this.lastSearch = this.props.location?.search || ''; this.load(); this.loadRollupStatus(); }
  componentWillUnmount() { if (this.rollupTimer) { clearInterval(this.rollupTimer); this.rollupTimer = null; } }

  // A visão fica montada ao trocar de aba. Querystring NOVA (link de outra visão) com a aba ativa →
  // refaz os filtros e recarrega. Querystring vazia (clique na aba) ou a que o próprio syncUrl
  // escreveu → mantém a consulta.
  componentDidUpdate() {
    const search = this.props.location?.search || '';
    if (this.props.active && search && search !== this.lastSearch) {
      this.lastSearch = search;
      this.setState({ filter: parseQuery(search) }, this.load);
    }
  }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }

  // ── Botão "Atualizar agora": roda o rollup do daily na API e se bloqueia até nextAllowedAt (servidor manda
  // retryAfterSeconds; a contagem é local, 1 s). Sem async em arrow class field (babel do react-scripts 4).
  loadRollupStatus() {
    UsageService.rollupStatus()
      .then((s) => this.applyRollup(s))
      .catch(() => this.setState({ rollup: { ...this.state.rollup, statusLoading: false } }));
  }
  applyRollup(s) {
    this.setState({ rollup: { ...this.state.rollup, ...s, running: false, statusLoading: false, retryAfter: s.retryAfterSeconds || 0 } }, this.tickRollup);
  }
  tickRollup() {
    if (this.rollupTimer) { clearInterval(this.rollupTimer); this.rollupTimer = null; }
    if (this.state.rollup.retryAfter <= 0) return;
    this.rollupTimer = setInterval(() => {
      const next = this.state.rollup.retryAfter - 1;
      this.setState({ rollup: { ...this.state.rollup, retryAfter: Math.max(0, next) } });
      if (next <= 0 && this.rollupTimer) { clearInterval(this.rollupTimer); this.rollupTimer = null; }
    }, 1000);
  }
  // Só aparece para o id 1 (canUnlock vem do servidor, que também barra a chamada de qualquer outro).
  unlockRollup() {
    this.setState({ rollup: { ...this.state.rollup, running: true } });
    UsageService.rollupUnlock()
      .then((s) => {
        this.applyRollup(s);
        this.setState({ snack: { open: true, variant: 'success', message: this.tr('usage.dash.rollup.unlocked', 'Espera cancelada; pode atualizar de novo.') } });
      })
      .catch((err) => this.setState({ rollup: { ...this.state.rollup, running: false }, snack: { open: true, variant: 'error', message: err.message } }));
  }

  runRollup() {
    this.setState({ rollup: { ...this.state.rollup, running: true } });
    UsageService.rollup()
      .then((s) => {
        this.applyRollup(s);
        if (s.ok) {
          // O rollup do dashboard é imediato; IPs, redes conhecidas, sessões e enriquecimento rodam no serviço de fundo em instantes.
          const worker = s.workerRequested
            ? this.tr('usage.dash.rollup.worker', 'IPs, redes e sessões serão atualizados em instantes pelo serviço de fundo.')
            : this.tr('usage.dash.rollup.workerOff', 'O serviço de fundo não foi acionado; IPs e redes seguem no ciclo de hora em hora.');
          this.setState({ snack: { open: true, variant: s.workerRequested ? 'success' : 'warning', message: `${this.tr('usage.dash.rollup.done', 'Consolidado agora')}. ${worker} ${this.tr('usage.dash.rollup.next', 'Próxima atualização manual em')} ${s.cooldownMinutes} min.` } });
          this.load();
        } else {
          this.setState({ snack: { open: true, variant: 'warning', message: s.error || this.tr('usage.dash.rollup.busy', 'Atualização já executada há pouco.') } });
        }
      })
      .catch((err) => this.setState({ rollup: { ...this.state.rollup, running: false }, snack: { open: true, variant: 'error', message: err.message } }));
  }


  // Atalho de KPI para a aba que detalha (mesmo período e usuário): exportações → linha do tempo; logins → Logins e sessões.
  goInvestigate(tab, extra) {
    const f = this.state.filter;
    const q = new URLSearchParams();
    q.set('start', moment(f.dateStart).format('YYYY-MM-DD')); q.set('end', moment(f.dateEnd).format('YYYY-MM-DD'));
    if (f.userId) q.set('userId', f.userId);
    Object.keys(extra).forEach((k) => q.set(k, extra[k]));
    this.props.history.push(`/${this.props.lang}/usage/${tab}?${q.toString()}`);
  }

  syncUrl() {
    const f = this.state.filter;
    const q = new URLSearchParams();
    q.set('preset', f.preset);
    if (f.preset === 'custom') { q.set('start', moment(f.dateStart).format('YYYY-MM-DD')); q.set('end', moment(f.dateEnd).format('YYYY-MM-DD')); }
    if (f.api) q.set('api', f.api);
    if (f.actionGroup) q.set('group', f.actionGroup);
    if (f.userId) { q.set('userId', f.userId); if (f.userName) q.set('userName', f.userName); }
    if (f.screenRoute) q.set('screen', f.screenRoute);
    this.lastSearch = `?${q.toString()}`;
    this.props.history.replace({ pathname: this.props.location.pathname, search: this.lastSearch });
  }

  onFilter(next) { this.setState({ filter: next }); }
  apply() { this.syncUrl(); this.load(); }
  setScreen(item) { this.setState((s) => ({ filter: { ...s.filter, screenRoute: s.filter.screenRoute === item.screenRoute ? '' : item.screenRoute } }), this.apply); }
  clearScreen() { this.setState((s) => ({ filter: { ...s.filter, screenRoute: '' } }), this.apply); }
  closeSnack() { this.setState({ snack: { ...this.state.snack, open: false } }); }
  goUser(u) {
    const f = this.state.filter;
    this.props.history.push(`/${this.props.lang}/usage/user-activity?userId=${u.userId}&userName=${encodeURIComponent(u.userName || '')}&start=${moment(f.dateStart).format('YYYY-MM-DD')}&end=${moment(f.dateEnd).format('YYYY-MM-DD')}`);
  }

  load() {
    const f = this.state.filter;
    this.setState({ loading: true, error: null });
    // Item 2: alertas abertos (independe do período; API antiga sem o endpoint = cartão não aparece).
    UsageService.alertsSummary().then((alerts) => this.setState({ alerts })).catch(() => this.setState({ alerts: null }));
    Promise.all([
      UsageService.summary(f), UsageService.byDay(f), UsageService.byHour(f), UsageService.topScreens(f, 12),
      UsageService.topActions(f, 12), UsageService.heaviest(f, this.state.heavyMode, this.state.heavyByScreen, 12),
      UsageService.topUsers(f, 25, this.serverSortKey(this.state.usersSort.key)), UsageService.byEntity(f, 12), UsageService.denials(f, 8), UsageService.byApi(f),
    ]).then(([summary, byDay, byHour, topScreens, topActions, heaviest, topUsers, byEntity, denials, byApi]) => {
      this.setState({ loading: false, summary, byDay, byHour, topScreens, topActions, heaviest, topUsers, byEntity, denials, byApi });
    }).catch((err) => {
      Api.kickoff(err);
      this.setState({ loading: false, error: err.message, snack: { open: true, variant: 'error', message: err.message } });
    });
  }

  loadHeaviest(mode, byScreen) {
    this.setState({ heavyMode: mode, heavyByScreen: byScreen });
    UsageService.heaviest(this.state.filter, mode, byScreen, 12).then((heaviest) => this.setState({ heaviest })).catch(() => {});
  }

  // Colunas que a API sabe ordenar (lista fechada no Reports). Nome e IPs ordenam só no navegador.
  serverSortKey(key) { return ['events', 'screens', 'reads', 'writes', 'exports', 'denials', 'errors'].includes(key) ? key : key === 'avgDurationMs' ? 'avg' : ''; }

  // Clique no cabeçalho: coluna NOVA → pede ao servidor os 25 maiores NAQUELA coluna (antes só reordenava os 25
  // mais ativos, e quem exportou muito navegando pouco nunca aparecia). Mesma coluna → inverte a direção local.
  sortUsers(key) {
    const same = this.state.usersSort.key === key;
    const dir = same && this.state.usersSort.dir === 'desc' ? 'asc' : 'desc';
    const server = this.serverSortKey(key);
    if (same || !server) { this.setState({ usersSort: { key, dir } }); return; }
    this.setState({ usersSort: { key, dir }, usersLoading: true });
    UsageService.topUsers(this.state.filter, 25, server)
      .then((topUsers) => this.setState({ topUsers, usersLoading: false }))
      .catch((err) => { Api.kickoff(err); this.setState({ usersLoading: false, snack: { open: true, variant: 'error', message: err.message } }); });
  }

  // ─────────────────────────── blocos ───────────────────────────

  renderKpis() {
    const { summary, loading } = this.state;
    const { intl } = this.props;
    // Sem resumo (carregando OU a chamada falhou): esqueleto. Nunca desreferenciar summary nulo.
    if (!summary || !summary.current) return <Grid container spacing={2}>{Array.from({ length: 8 }).map((_, i) => <Grid item xs={6} md={3} lg={1.5} key={i}><Skeleton variant="rounded" height={92} animation={loading ? 'wave' : false} /></Grid>)}</Grid>;
    const c = summary.current; const p = summary.previous || {};
    const errorRate = c.durationSamples > 0 ? (c.errors / c.durationSamples) * 100 : 0;
    const prevRate = p.durationSamples > 0 ? (p.errors / p.durationSamples) * 100 : 0;
    const a = this.state.alerts;
    const openAlerts = a ? a.openCritical + a.openWarning + a.openInfo : 0;
    const cards = [
      // Item 2: alertas de segurança abertos (vermelho com crítico; laranja só com avisos). Clique abre a aba Alertas.
      ...(a ? [{ title: this.tr('usage.dash.kpi.alerts', 'Alertas abertos'), value: openAlerts, icon: <NotificationsActiveIcon />, color: a.openCritical ? '#c62828' : openAlerts ? '#ef6c00' : '#607D8B',
        subtitle: a.openCritical ? `${fmtInt(a.openCritical)} ${this.tr('usage.dash.kpi.alerts.critical', 'crítico(s)')}` : '', onClick: () => this.props.history.push(`/${this.props.lang}/usage/alerts`) }] : []),
      { title: this.tr('usage.dash.kpi.events', 'Eventos'), value: c.events, previous: p.events, icon: <EventIcon />, color: '#3F51B4' },
      { title: this.tr('usage.dash.kpi.activeUsers', 'Usuários ativos'), value: c.activeUsers, previous: p.activeUsers, icon: <PeopleIcon />, color: '#009688' },
      { title: this.tr('usage.dash.kpi.screens', 'Telas abertas'), value: c.screens, previous: p.screens, icon: <WebIcon />, color: '#9C27B0' },
      { title: this.tr('usage.dash.kpi.reads', 'Leituras'), value: c.reads, previous: p.reads, icon: <VisibilityIcon />, color: '#3F51B4' },
      { title: this.tr('usage.dash.kpi.writes', 'Escritas'), value: c.writes, previous: p.writes, icon: <EditIcon />, color: '#8BC34A' },
      { title: this.tr('usage.dash.kpi.exports', 'Exportações'), value: c.exports, previous: p.exports, icon: <FileDownloadIcon />, color: '#FF9800', onClick: () => this.goInvestigate('user-activity', { actionKind: 'export' }) },
      { title: this.tr('usage.dash.kpi.logins', 'Logins'), value: c.logins, previous: p.logins, icon: <LoginIcon />, color: '#009688', onClick: () => this.goInvestigate('sessions', {}),
        subtitle: c.loginFailures ? `${fmtInt(c.loginFailures)} ${this.tr('usage.dash.kpi.loginFailures', 'falha(s)')}` : '' },
      { title: this.tr('usage.dash.kpi.denials', 'Recusas'), value: c.denials, previous: p.denials, icon: <BlockIcon />, color: '#F44336', invert: true },
      { title: this.tr('usage.dash.kpi.errors', 'Erros 5xx'), value: c.errors, previous: p.errors, icon: <ErrorIcon />, color: '#B71C1C', invert: true,
        subtitle: `${this.tr('usage.dash.kpi.rate', 'taxa')} ${fmtPct(errorRate)}${p.durationSamples ? ` (${this.tr('usage.dash.kpi.before', 'antes')} ${fmtPct(prevRate)})` : ''}` },
    ];
    return (
      <Grid container spacing={2}>
        {cards.map((k) => <Grid item xs={6} sm={4} md={3} lg={4 / 3} key={k.title}><UsageKpiCard intl={intl} {...k} /></Grid>)}
        <Grid item xs={12}>
          <Typography variant="caption" style={{ color: '#888' }}>
            {this.tr('usage.dash.avgLine', 'Duração média das chamadas')}: <b>{fmtMs(c.avgDurationMs)}</b> · {this.tr('usage.dash.maxLine', 'máximo')} <b>{fmtMs(c.maxDurationMs)}</b>
            {c.updatedAt ? ` · ${this.tr('usage.dash.updated', 'resumo atualizado')} ${moment(c.updatedAt).fromNow()}` : ''}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  chartCard(title, subtitle, body, right) {
    return (
      <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, height: '100%' }}>
        <CardContent>
          <UsageBlockTitle title={title} subtitle={subtitle} right={right} />
          {body}
        </CardContent>
      </Card>
    );
  }

  renderByDay() {
    const { intl } = this.props;
    const data = this.state.byDay.map((d) => ({ ...d, label: moment(d.day).format('DD/MM') }));
    if (!data.length) return <UsageEmpty intl={intl} compact />;
    return (
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" /><YAxis yAxisId="l" allowDecimals={false} /><YAxis yAxisId="r" orientation="right" allowDecimals={false} />
          <ChartTooltip formatter={(v, n) => [fmtInt(v), n]} /><Legend />
          <Bar yAxisId="l" dataKey="denials" name={this.tr('usage.dash.series.denials', 'Recusas')} fill="#F4433655" />
          <Line yAxisId="l" type="monotone" dataKey="events" name={this.tr('usage.dash.series.events', 'Eventos')} stroke="#3F51B4" strokeWidth={2} dot={false} />
          <Line yAxisId="r" type="monotone" dataKey="users" name={this.tr('usage.dash.series.users', 'Usuários')} stroke="#009688" strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  renderByHour() {
    const { intl } = this.props;
    const { hourView, byHour } = this.state;
    const toggle = (
      <ToggleButtonGroup size="small" exclusive value={hourView} onChange={(e, v) => v && this.setState({ hourView: v })}>
        <ToggleButton value="bars">{this.tr('usage.dash.byHour.bars', 'Por hora')}</ToggleButton><ToggleButton value="heat">{this.tr('usage.dash.byHour.heat', 'Mapa de calor')}</ToggleButton>
      </ToggleButtonGroup>
    );
    const bars = Array.from({ length: 24 }, (_, h) => ({ hour: `${h}h`, events: (byHour.byHour.find((x) => x.hour === h) || {}).events || 0 }));
    const body = hourView === 'heat' ? <UsageHeatmap intl={intl} cells={byHour.heatmap} /> : (
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={bars}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="hour" interval={1} /><YAxis allowDecimals={false} /><ChartTooltip formatter={(v) => fmtInt(v)} />
          <Bar dataKey="events" name={this.tr('usage.dash.series.events', 'Eventos')} fill="#277afe" radius={[3, 3, 0, 0]} /></BarChart>
      </ResponsiveContainer>
    );
    return this.chartCard(this.tr('usage.dash.byHour.title', 'Horário de pico'), this.tr('usage.dash.byHour.subtitle', 'Quando o sistema é mais usado'), body, toggle);
  }

  renderHeaviest() {
    const { intl } = this.props;
    const { heavyMode, heavyByScreen, heaviest } = this.state;
    const valueKey = heavyMode === 'total' ? 'totalDurationMs' : heavyMode === 'max' ? 'maxDurationMs' : 'avgDurationMs';
    const modeLabel = { avg: this.tr('usage.dash.heaviest.avg', 'Média'), total: this.tr('usage.dash.heaviest.total', 'Tempo total'), max: this.tr('usage.dash.heaviest.max', 'Máximo') };
    const right = (
      <Box display="flex" style={{ gap: 6 }}>
        <ToggleButtonGroup size="small" exclusive value={heavyMode} onChange={(e, v) => v && this.loadHeaviest(v, heavyByScreen)}>
          {HEAVY_MODES.map((m) => <ToggleButton key={m} value={m}>{modeLabel[m]}</ToggleButton>)}
        </ToggleButtonGroup>
        <ToggleButton size="small" value="screen" selected={heavyByScreen} onChange={() => this.loadHeaviest(heavyMode, !heavyByScreen)}>{this.tr('usage.dash.heaviest.byScreen', 'por tela')}</ToggleButton>
      </Box>
    );
    const items = heaviest.map((h) => ({ ...h, label: heavyByScreen && h.screenRoute ? `${h.route}  ·  ${h.screenRoute}` : h.route }));
    const subtitle = heavyMode === 'total' ? this.tr('usage.dash.heaviest.sub.total', 'Onde o servidor gasta tempo (chamadas × média)')
      : heavyMode === 'max' ? this.tr('usage.dash.heaviest.sub.max', 'Pior caso registrado') : this.tr('usage.dash.heaviest.sub.avg', 'Duração média por chamada');
    return this.chartCard(this.tr('usage.dash.heaviest.title', 'Endpoints mais pesados'), subtitle,
      <UsageRankBar intl={intl} items={items} labelKey="label" valueKey={valueKey} format={fmtMs} color={(it) => durationColor(it.avgDurationMs)} secondary={(it) => `${fmtInt(it.count)}×`} />, right);
  }

  renderDenials() {
    const { intl } = this.props;
    const { denials } = this.state;
    const pie = denials.byKind.map((k) => ({ name: kindLabel(intl, k.actionKind), kind: k.actionKind, value: k.count }));
    const days = {};
    denials.byDay.forEach((d) => { const k = moment(d.day).format('DD/MM'); days[k] = days[k] || { label: k }; days[k][d.actionKind] = (days[k][d.actionKind] || 0) + d.count; });
    const series = Object.values(days);
    const body = pie.length === 0 ? <UsageEmpty intl={intl} compact title={this.tr('usage.dash.denials.none', 'Nenhuma recusa no período')} /> : (
      <Grid container spacing={1}>
        <Grid item xs={12} md={5}>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart><Pie data={pie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>{pie.map((p) => <Cell key={p.kind} fill={KIND_COLORS[p.kind] || '#999'} />)}</Pie><ChartTooltip formatter={(v) => fmtInt(v)} /><Legend iconSize={8} /></PieChart>
          </ResponsiveContainer>
        </Grid>
        <Grid item xs={12} md={7}>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={series}><XAxis dataKey="label" /><YAxis allowDecimals={false} /><ChartTooltip />{DENIAL_KINDS.map((k) => <Bar key={k} dataKey={k} name={kindLabel(intl, k)} stackId="d" fill={KIND_COLORS[k]} />)}</BarChart>
          </ResponsiveContainer>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" style={{ color: '#888' }}>{this.tr('usage.dash.denials.top', 'Top ofensores')}</Typography>
          <UsageRankBar intl={intl} items={denials.byUser} labelKey="userName" valueKey="denials" color="#F44336" onClick={this.goUser} />
        </Grid>
      </Grid>
    );
    return this.chartCard(this.tr('usage.dash.denials.title', 'Recusas e bloqueios'), this.tr('usage.dash.denials.subtitle', 'IDOR, papel, bloqueio automático e 429'), body,
      <Button size="small" onClick={() => this.props.history.push(`/${this.props.lang}/usage/blocked`)}>{this.tr('usage.dash.denials.all', 'ver tudo')}</Button>);
  }

  renderEntities() {
    const { intl } = this.props;
    const data = this.state.byEntity;
    if (!data.length) return this.chartCard(this.tr('usage.dash.entities.short', 'Entidades'), this.tr('usage.dash.entities.what', 'O que é tocado'), <UsageEmpty intl={intl} compact />);
    return this.chartCard(this.tr('usage.dash.entities.title', 'Entidades mais acessadas'), this.tr('usage.dash.entities.subtitle', 'Por tipo, com quebra por ação'),
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 26)}>
        <BarChart data={data} layout="vertical" margin={{ left: 40 }}><XAxis type="number" allowDecimals={false} /><YAxis type="category" dataKey="entityType" width={140} tick={{ fontSize: 11 }} /><ChartTooltip formatter={(v) => fmtInt(v)} /><Legend />
          <Bar dataKey="reads" name={kindLabel(intl, 'read')} stackId="e" fill={KIND_COLORS.read} /><Bar dataKey="writes" name={kindLabel(intl, 'write')} stackId="e" fill={KIND_COLORS.write} /><Bar dataKey="exports" name={kindLabel(intl, 'export')} stackId="e" fill={KIND_COLORS.export} /></BarChart>
      </ResponsiveContainer>);
  }

  renderByApi() {
    const { intl } = this.props;
    const data = this.state.byApi.map((a) => ({ name: a.api, value: a.events }));
    if (!data.length) return this.chartCard(this.tr('usage.dash.byApi.short', 'Por API'), '', <UsageEmpty intl={intl} compact />);
    return this.chartCard(this.tr('usage.dash.byApi.title', 'Distribuição por API'), this.tr('usage.dash.byApi.subtitle', 'Inclui as telas (front)'),
      <ResponsiveContainer width="100%" height={200}>
        <PieChart><Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>{data.map((d) => <Cell key={d.name} fill={API_COLORS[d.name] || '#999'} />)}</Pie><ChartTooltip formatter={(v) => fmtInt(v)} /><Legend iconSize={8} /></PieChart>
      </ResponsiveContainer>);
  }

  renderUsers() {
    const { intl } = this.props;
    const { topUsers, usersSort, usersLoading } = this.state;
    const cols = [
      ['userName', this.tr('usage.col.user', 'Usuário')], ['events', this.tr('usage.col.events', 'Eventos')], ['screens', this.tr('usage.col.screens', 'Telas')],
      ['reads', this.tr('usage.col.reads', 'Leituras')], ['writes', this.tr('usage.col.writes', 'Escritas')], ['exports', this.tr('usage.col.exports', 'Exportações')],
      ['resultRows', this.tr('usage.col.rows', 'Linhas')], // item 5: linhas devolvidas ao usuário no período
      ['denials', this.tr('usage.col.denials', 'Recusas')], ['errors', this.tr('usage.col.errors', 'Erros')], ['avgDurationMs', this.tr('usage.col.avg', 'Média')],
      ['ips', 'IPs'],
    ];
    const rows = [...topUsers].sort((a, b) => {
      const va = a[usersSort.key]; const vb = b[usersSort.key];
      const r = typeof va === 'string' ? String(va).localeCompare(String(vb)) : (Number(va) || 0) - (Number(vb) || 0);
      return usersSort.dir === 'asc' ? r : -r;
    });
    // 'Ordenar por' explícito (o clique no cabeçalho continua valendo): a lista é refeita no servidor com os 25
    // maiores da coluna escolhida, com spinner enquanto carrega e o subtítulo dizendo o critério.
    const ORDER_KEYS = ['events', 'screens', 'reads', 'writes', 'exports', 'denials', 'errors', 'avgDurationMs'];
    const orderKey = ORDER_KEYS.includes(usersSort.key) ? usersSort.key : 'events';
    const orderLabel = (cols.find(([k]) => k === orderKey) || [])[1] || '';
    const orderSelect = (
      <TextField select size="small" label={this.tr('usage.dash.users.orderBy', 'Ordenar por')} value={orderKey} InputLabelProps={{ shrink: true }} SelectProps={{ native: true }} style={{ minWidth: 170 }}
        onChange={(e) => { const k = e.target.value; if (k !== usersSort.key) this.sortUsers(k); }} disabled={usersLoading}>
        {ORDER_KEYS.map((k) => <option key={k} value={k}>{(cols.find(([c]) => c === k) || [])[1]}</option>)}
      </TextField>
    );
    const usersRight = (
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        {orderSelect}
        <UsageExportButton intl={intl} tab="dashboard-usuarios" onNotify={(v, m) => this.setState({ snack: { open: true, variant: v, message: m } })} getRows={() => rows}
          filters={{ dateStart: this.state.filter.dateStart, dateEnd: this.state.filter.dateEnd, api: this.state.filter.api, userId: this.state.filter.userId, group: this.state.filter.actionGroup, orderBy: orderKey }}
          columns={[{ header: 'usuario_id', value: (r) => r.userId }, { header: 'usuario', value: (r) => r.userName }, { header: 'eventos', value: (r) => r.events }, { header: 'telas', value: (r) => r.screens }, { header: 'leituras', value: (r) => r.reads }, { header: 'escritas', value: (r) => r.writes }, { header: 'exportacoes', value: (r) => r.exports }, { header: 'recusas', value: (r) => r.denials }, { header: 'erros_5xx', value: (r) => r.errors }, { header: 'duracao_media_ms', value: (r) => r.avgDurationMs }, { header: 'ips', value: (r) => r.ips }]} />
      </Box>
    );
    if (!rows.length) return this.chartCard(this.tr('usage.dash.users.title', 'Usuários'), '', <UsageEmpty intl={intl} compact />, usersRight);
    return this.chartCard(this.tr('usage.dash.users.title', 'Usuários'), `${this.tr('usage.dash.users.topBy', 'os 25 maiores por')} ${orderLabel} · ${this.tr('usage.dash.users.subtitle', 'clique num usuário para abrir a linha do tempo')}`,
      <Box style={{ overflowX: 'auto', position: 'relative' }}>
        {usersLoading && <Box display="flex" alignItems="center" justifyContent="center" style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', zIndex: 2 }}><CircularProgress size={28} color="secondary" /></Box>}
        <Table size="small">
          <TableHead><TableRow>{cols.map(([k, l]) => <TableCell key={k} align={k === 'userName' || k === 'ips' ? 'left' : 'right'}><TableSortLabel active={usersSort.key === k} direction={usersSort.key === k ? usersSort.dir : 'desc'} onClick={() => this.sortUsers(k)}>{l}</TableSortLabel></TableCell>)}</TableRow></TableHead>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.userId} hover style={{ cursor: 'pointer' }} onClick={() => this.goUser(u)}>
                <TableCell><UsageEllipsisCell text={u.userName} maxWidth={220} /></TableCell>
                {cols.slice(1).map(([k]) => (k === 'ips'
                  ? <TableCell key={k}><UsageEllipsisCell text={u.ips === null ? this.tr('usage.dash.users.ipsTooLong', 'período > 90 dias') : (u.ips || '—')} title={u.ips === null ? this.tr('usage.dash.users.ipsTooLongTip', 'Os IPs vêm do histórico detalhado, lido só em períodos de até 90 dias') : (u.ips || '')} maxWidth={220} color="#666" /></TableCell>
                  : <TableCell key={k} align="right" style={{ fontVariantNumeric: 'tabular-nums', color: k === 'denials' && u.denials > 0 ? '#c62828' : undefined }}>{k === 'avgDurationMs' ? fmtMs(u[k]) : fmtInt(u[k])}</TableCell>))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>, usersRight);
  }

  renderRollup() {
    const { rollup } = this.state;
    const waiting = rollup.retryAfter > 0;
    const mm = Math.floor(rollup.retryAfter / 60); const sec = String(rollup.retryAfter % 60).padStart(2, '0');
    const last = rollup.lastRollupAt
      ? `${moment(rollup.lastRollupAt).fromNow()} (${moment(rollup.lastRollupAt).format('DD/MM HH:mm')})`
      : this.tr('usage.dash.rollup.never', 'ainda não consolidado');
    return (
      <Box display="flex" alignItems="center" flexWrap="wrap" style={{ marginTop: 8, gap: 12 }}>
        <Button size="small" variant="outlined" color="secondary" onClick={this.runRollup}
          disabled={rollup.running || waiting || rollup.statusLoading}
          startIcon={rollup.running ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}>
          {waiting ? `${this.tr('usage.dash.rollup.wait', 'Libera em')} ${mm}:${sec}` : this.tr('usage.dash.rollup.button', 'Atualizar agora')}
        </Button>
        {/* "Cancelar espera": só o administrador global da Mexx (id 1), e o servidor confere de novo. Para testar sem esperar 10 min. */}
        {waiting && rollup.canUnlock && (
          <Button size="small" variant="text" color="secondary" onClick={this.unlockRollup} disabled={rollup.running} style={{ textTransform: 'none' }}>
            {this.tr('usage.dash.rollup.unlock', 'Cancelar espera')}
          </Button>
        )}
        <Typography variant="caption" style={{ color: '#888' }}>
          {this.tr('usage.dash.rollup.last', 'Última consolidação')}: {last} · {this.tr('usage.dash.rollup.hint2', 'os painéis leem o consolidado, refeito a cada hora; o botão refaz agora o consolidado e pede ao serviço de fundo os IPs, redes e sessões')}
        </Typography>
      </Box>
    );
  }


  render() {
    const { intl } = this.props;
    const { filter, loading, summary, topScreens, topActions, snack } = this.state;
    return (
      <Box>
        <UsagePeriodFilter showIp={false} intl={intl} value={filter} onChange={this.onFilter} onApply={this.apply} applying={loading}
          disabledFields={{ ip: this.tr('usage.noIp.rollup', 'O resumo diário não guarda IP. Para investigar por IP use as abas Tráfego por IP, Linha do tempo ou Recusas.') }}
          linkParams={{ preset: 'custom', group: filter.actionGroup, screen: filter.screenRoute }} />
        {this.renderRollup()}
        {filter.screenRoute && (
          <Box style={{ marginTop: 8 }}>
            <Chip label={`${this.tr('usage.dash.screenChip', 'Tela')}: ${filter.screenRoute}`} onDelete={this.clearScreen} color="primary" size="small" />
            <Typography variant="caption" style={{ color: '#888', marginLeft: 8 }}>{this.tr('usage.dash.crossFilter', 'filtro cruzado aplicado aos blocos')}</Typography>
          </Box>
        )}
        <Box style={{ marginTop: 16 }}><Box style={{ marginBottom: 4 }}><UsageSourceTag intl={intl} source="rollup" ctx={this.props.usageContext} /></Box>{this.renderKpis()}</Box>
        {loading && summary && <Box display="flex" justifyContent="center" style={{ padding: 8 }}><CircularProgress size={22} color="secondary" /></Box>}
        {summary && summary.current.events === 0 && !loading ? (
          <Box style={{ marginTop: 16 }}><UsageEmpty intl={intl} icon={<EventIcon style={{ fontSize: 56 }} />} /></Box>
        ) : (
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12} lg={8}>{this.chartCard(this.tr('usage.dash.byDay.title', 'Evolução no período'), this.tr('usage.dash.byDay.subtitle', 'Eventos, usuários e recusas por dia'), this.renderByDay())}</Grid>
            <Grid item xs={12} lg={4}>{this.renderByHour()}</Grid>
            <Grid item xs={12} md={6}>{this.chartCard(this.tr('usage.dash.topScreens.title', 'Telas mais acessadas'), this.tr('usage.dash.topScreens.subtitle', 'Por visitas · clique para cruzar os outros blocos'),
              <UsageRankBar intl={intl} items={topScreens} labelKey="screenRoute" valueKey="visits" color="#9C27B0" onClick={this.setScreen} selected={filter.screenRoute} secondary={(it) => `${fmtInt(it.requests)} req · ${fmtMs(it.avgDurationMs)}`} />,
              <Button size="small" onClick={() => this.props.history.push(`/${this.props.lang}/usage/screens?start=${moment(filter.dateStart).format('YYYY-MM-DD')}&end=${moment(filter.dateEnd).format('YYYY-MM-DD')}`)}>{this.tr('usage.dash.topScreens.all', 'ver todas')}</Button>)}</Grid>
            <Grid item xs={12} md={6}>{this.chartCard(this.tr('usage.dash.topActions.title', 'Ações mais realizadas'), filter.screenRoute ? `${this.tr('usage.dash.topActions.inScreen', 'Dentro da tela')} ${filter.screenRoute}` : this.tr('usage.dash.topActions.subtitle', 'Por rota da API'),
              <UsageRankBar intl={intl} items={topActions} labelKey="route" valueKey="count" color="#3F51B4" secondary={(it) => `${fmtMs(it.avgDurationMs)}${it.errors ? ` · ${it.errors} ${this.tr('usage.col.errors', 'Erros').toLowerCase()}` : ''}`} />)}</Grid>
            <Grid item xs={12} md={6}>{this.renderHeaviest()}</Grid>
            <Grid item xs={12} md={6}>{this.renderDenials()}</Grid>
            <Grid item xs={12} md={7}>{this.renderEntities()}</Grid>
            <Grid item xs={12} md={5}>{this.renderByApi()}</Grid>
            <Grid item xs={12}>{this.renderUsers()}</Grid>
          </Grid>
        )}
        <CustomizedSnackbars variant={snack.variant} message={snack.message} isOpen={snack.open} toClose={this.closeSnack} />
      </Box>
    );
  }
}
